import { Link } from "react-router-dom";
import { CompanyInquiryForm } from "@/components/CompanyInquiryForm";
import { ArrowUpRight, Award, Briefcase, Building2, CheckCircle2, GraduationCap, Handshake, Languages, Mail, Phone, Sparkles, UserRoundCheck, Users } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const CONTACT_EMAIL = "corporate@codewithsankalp.com";
const URS_PHOTO = "/urs-keller.jpg";

export default function Companies() {
  return (
    <div className="min-h-screen bg-[#09090B] text-white">
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-zinc-900">
        <div className="absolute inset-0 grid-bg-adult opacity-60" />
        <div className="absolute inset-0 grain opacity-40 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 pt-24 pb-20">
          <div className="text-[11px] uppercase tracking-[0.32em] text-amber-400 font-adult-mono mb-5 flex items-center gap-2">
            <Building2 className="w-3.5 h-3.5" /> For Companies
          </div>
          <h1 data-testid="companies-hero-title" className="font-adult-heading text-5xl md:text-7xl font-semibold tracking-tight leading-[0.98] max-w-4xl">
            Train your team. <br />
            <span className="font-adult-serif italic text-amber-400">Hire ours.</span>
          </h1>
          <p className="mt-6 text-lg text-zinc-300 max-w-2xl leading-relaxed">
            We partner with growing companies to upskill their workforce and place job-ready talent from our cohorts. Every engagement is bespoke — priced after a short discovery call.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a data-testid="companies-cta-contact" href="#contact" className="inline-flex items-center gap-2 rounded-full bg-amber-500 hover:bg-amber-400 text-black font-semibold px-6 py-3 text-sm glow-amber transition-smooth">
              Book a discovery call <ArrowUpRight className="w-4 h-4" />
            </a>
            <a href={`mailto:${CONTACT_EMAIL}`} className="inline-flex items-center gap-2 rounded-full border border-zinc-800 hover:border-amber-500/50 px-6 py-3 text-sm text-zinc-200 transition-smooth">
              <Mail className="w-4 h-4" /> {CONTACT_EMAIL}
            </a>
          </div>
        </div>
      </section>

      {/* Two pillars */}
      <section className="max-w-7xl mx-auto px-6 py-20 grid md:grid-cols-2 gap-5">
        {/* Corporate training */}
        <div data-testid="companies-pillar-training" className="rounded-3xl border border-zinc-800 bg-[#0d0d0f] p-8">
          <div className="w-12 h-12 rounded-2xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center text-amber-400 mb-5">
            <GraduationCap className="w-6 h-6" />
          </div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-amber-400 font-adult-mono">Pillar 01</div>
          <h2 className="font-adult-heading text-3xl md:text-4xl font-semibold tracking-tight mt-2">Corporate Training</h2>
          <p className="mt-4 text-zinc-400 leading-relaxed">
            Custom cohorts for your engineering, analytics, product & operations teams. Delivered live by industry-trained instructors, tailored to your stack and goals.
          </p>
          <ul className="mt-6 space-y-3">
            {[
              "Bespoke curriculum designed with your L&D team",
              "Live cohorts — evenings or weekends, on-site or remote",
              "Progress dashboards & completion certificates",
              "Post-training coaching for 30 days",
            ].map((t) => (
              <li key={t} className="flex items-start gap-3 text-sm text-zinc-300">
                <CheckCircle2 className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                {t}
              </li>
            ))}
          </ul>
        </div>

        {/* Hire our talent */}
        <div data-testid="companies-pillar-hiring" className="rounded-3xl border border-zinc-800 bg-[#0d0d0f] p-8">
          <div className="w-12 h-12 rounded-2xl bg-violet-500/10 border border-violet-500/30 flex items-center justify-center text-violet-400 mb-5">
            <Handshake className="w-6 h-6" />
          </div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-violet-400 font-adult-mono">Pillar 02</div>
          <h2 className="font-adult-heading text-3xl md:text-4xl font-semibold tracking-tight mt-2">Hire Our Talent</h2>
          <p className="mt-4 text-zinc-400 leading-relaxed">
            Every graduate from our flagship bootcamp is job-ready — vetted through capstones, interviews, and real project reviews. Skip the hiring funnel; interview pre-screened, ready-to-hire talent.
          </p>
          <ul className="mt-6 space-y-3">
            {[
              "Curated shortlists in Data / Analytics / Python / SQL",
              "Portfolio & capstone links shared upfront",
              "Skills-tested candidates, no rescreening needed",
              "Structured 2-week trial with any hire",
            ].map((t) => (
              <li key={t} className="flex items-start gap-3 text-sm text-zinc-300">
                <CheckCircle2 className="w-4 h-4 text-violet-400 shrink-0 mt-0.5" />
                {t}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Signature offering — Urs Keller Swiss German */}
      <section data-testid="companies-signature-course" className="border-y border-zinc-900 bg-black/40">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="text-[11px] uppercase tracking-[0.32em] text-amber-400 font-adult-mono mb-4 flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5" /> Signature corporate course
          </div>
          <h2 className="font-adult-heading text-4xl md:text-5xl font-semibold tracking-tight max-w-3xl">
            Professional <span className="font-adult-serif italic text-amber-400">Swiss German</span> for Global Teams
          </h2>
          <p className="mt-5 text-zinc-400 max-w-2xl leading-relaxed">
            Break the language barrier for teams working with Swiss clients or relocating to Switzerland. Live, immersive Swiss German taught by a retired professor and certified language expert.
          </p>

          <div className="mt-12 grid md:grid-cols-5 gap-8 items-start">
            {/* Instructor */}
            <div className="md:col-span-2">
              <div className="relative rounded-3xl overflow-hidden border border-zinc-800 bg-zinc-950">
                <img
                  data-testid="companies-instructor-photo"
                  src={URS_PHOTO}
                  alt="Urs Keller — Retired Professor & Certified Swiss German Instructor"
                  className="w-full aspect-[4/5] object-cover"
                />
              </div>
              <div className="mt-5">
                <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500 font-adult-mono">Your instructor</div>
                <div data-testid="companies-instructor-name" className="font-adult-heading text-2xl font-semibold mt-1">Urs Keller</div>
                <div className="text-sm text-amber-400 font-adult-mono mt-1">Retired Professor · Certified Swiss German Instructor</div>
              </div>
            </div>

            {/* Details */}
            <div className="md:col-span-3 space-y-6">
              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  { icon: <Languages className="w-4 h-4" />, k: "Language", v: "Schwyzerdütsch (Swiss German)" },
                  { icon: <Users className="w-4 h-4" />, k: "Format", v: "Live cohort · small groups" },
                  { icon: <Award className="w-4 h-4" />, k: "Level", v: "Beginner → Business Fluent" },
                  { icon: <UserRoundCheck className="w-4 h-4" />, k: "Ideal for", v: "Teams working with Swiss clients or offices" },
                ].map((m) => (
                  <div key={m.k} className="rounded-2xl border border-zinc-800 p-4 bg-[#0d0d0f]">
                    <div className="text-amber-400 mb-2">{m.icon}</div>
                    <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-500 font-adult-mono">{m.k}</div>
                    <div className="text-sm text-zinc-200 mt-1">{m.v}</div>
                  </div>
                ))}
              </div>

              <div className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-5">
                <div className="flex items-start gap-3">
                  <Sparkles className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
                  <div>
                    <div className="text-sm text-zinc-200 leading-relaxed">
                      Curriculum is <strong className="text-amber-400">custom-built</strong> for each company — business etiquette, workplace vocabulary, or client-facing conversation.
                    </div>
                    <div className="mt-3 text-xs text-zinc-500 font-adult-mono uppercase tracking-[0.24em]">Pricing shared after a discovery call</div>
                  </div>
                </div>
              </div>

              <a href="#contact" className="inline-flex items-center gap-2 rounded-full bg-amber-500 hover:bg-amber-400 text-black font-semibold px-6 py-3 text-sm glow-amber transition-smooth">
                Enquire about this course <ArrowUpRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Contact form */}
      <section id="contact" className="max-w-7xl mx-auto px-6 py-20 grid md:grid-cols-5 gap-10">
        <div className="md:col-span-2">
          <div className="text-[11px] uppercase tracking-[0.32em] text-amber-400 font-adult-mono mb-4">Let's talk</div>
          <h2 className="font-adult-heading text-4xl font-semibold tracking-tight">Every partnership starts with a call.</h2>
          <p className="mt-4 text-zinc-400 leading-relaxed">
            Tell us a bit about your team and what you're solving for. We'll share the right playbook and a custom quote — no cookie-cutter pricing.
          </p>
          <div className="mt-8 space-y-3 text-sm text-zinc-300">
            <a href={`mailto:${CONTACT_EMAIL}`} className="flex items-center gap-3 hover:text-amber-400 transition-smooth">
              <Mail className="w-4 h-4 text-amber-400" /> {CONTACT_EMAIL}
            </a>
            <div className="flex items-center gap-3">
              <Briefcase className="w-4 h-4 text-amber-400" /> B2B engagements only
            </div>
          </div>
        </div>

        <CompanyInquiryForm />
      </section>

      <div className="max-w-7xl mx-auto px-6 pb-10 text-center">
        <Link to="/" className="text-sm text-zinc-500 hover:text-amber-400 transition-smooth font-adult-mono">← back to home</Link>
      </div>

      <Footer />
    </div>
  );
}

